// Peer-reviewed citations for each NR-05 active.
// Each entry contains study metadata used to render the /science page.

export const SCIENCE = [
  {
    id: "nr-chloride",
    code: "01·A",
    name: "Nicotinamide Riboside Chloride",
    short: "NR Chloride",
    dose: "350mg",
    role: "NAD+ precursor · NR-05",
    thesis:
      "NR is an orally bioavailable precursor that elevates intracellular NAD+ — the central cofactor of cellular energy metabolism and sirtuin signaling.",
    mechanism: [
      "Phosphorylated by NRK1/2 to NMN, then converted to NAD+.",
      "Bypasses rate-limiting steps in the de novo and salvage pathways.",
      "Sustained dosing elevates whole-blood NAD+ ~40–60% in healthy adults.",
    ],
    studies: [
      {
        title:
          "Nicotinamide riboside is uniquely and orally bioavailable in mice and humans",
        authors: "Trammell, Schmidt, Weidemann et al.",
        journal: "Nature Communications",
        year: 2016,
        doi: "10.1038/ncomms12948",
        finding:
          "First human pharmacokinetic study showing NR elevates blood NAD+ metabolome dose-dependently after single oral doses.",
      },
      {
        title:
          "Chronic nicotinamide riboside supplementation is well-tolerated and elevates NAD+ in healthy middle-aged and older adults",
        authors: "Martens, Denman, Mazzo et al.",
        journal: "Nature Communications",
        year: 2018,
        doi: "10.1038/s41467-018-03421-7",
        finding:
          "6-week 500mg BID NR supplementation increased NAD+ ~60% and lowered systolic blood pressure in adults with elevated BP.",
      },
    ],
  },
  {
    id: "nmn",
    code: "01·B",
    name: "Nicotinamide Mononucleotide",
    short: "NMN",
    dose: "500mg",
    role: "NAD+ precursor · NMN-05",
    thesis:
      "NMN is the direct metabolic precursor to NAD+ — one enzymatic step from the cofactor itself. Human trials show consistent elevation of blood NAD+ metabolome with sustained dosing.",
    mechanism: [
      "Converted directly to NAD+ by NMNAT enzymes in the cell.",
      "Elevates NAD+ in blood, muscle and skin biopsies in human trials.",
      "Slc12a8 and CD73/CD38-mediated cellular uptake pathways characterized.",
    ],
    studies: [
      {
        title:
          "Nicotinamide mononucleotide increases muscle insulin sensitivity in prediabetic women",
        authors: "Yoshino, Baur, Imai et al.",
        journal: "Science",
        year: 2021,
        doi: "10.1126/science.abe9985",
        finding:
          "10-week 250mg/day NMN increased muscle insulin sensitivity and remodeling in overweight/obese prediabetic women.",
      },
      {
        title:
          "Chronic nicotinamide mononucleotide supplementation elevates blood nicotinamide adenine dinucleotide levels and alters muscle function in healthy older men",
        authors: "Igarashi, Nakagawa-Nagahama, Miura et al.",
        journal: "npj Aging",
        year: 2022,
        doi: "10.1038/s41514-022-00084-z",
        finding:
          "12-week 250mg/day NMN increased whole-blood NAD+ and improved gait speed and grip strength in older adults.",
      },
      {
        title:
          "The efficacy and safety of β-nicotinamide mononucleotide (NMN) supplementation in healthy middle-aged adults: a randomized, multicenter, double-blind, placebo-controlled study",
        authors: "Yi, Park, Choi et al.",
        journal: "GeroScience",
        year: 2023,
        doi: "10.1007/s11357-022-00705-1",
        finding:
          "60-day 300/600/900mg NMN was safe and dose-dependently elevated blood NAD+ in healthy middle-aged adults.",
      },
    ],
  },
  {
    id: "pterostilbene",
    code: "02",
    name: "Pterostilbene",
    short: "Pterostilbene",
    dose: "50mg",
    role: "Sirtuin signal",
    thesis:
      "A dimethylated analog of resveratrol with markedly superior oral bioavailability. Modulates sirtuin signaling alongside elevated NAD+ availability.",
    mechanism: [
      "Activates SIRT1 and modulates AMPK signaling.",
      "Reduces oxidized LDL and inflammatory markers in human trials.",
      "Half-life ~105 minutes — substantially higher exposure vs. resveratrol.",
    ],
    studies: [
      {
        title:
          "A review of pterostilbene antioxidant activity and disease modification",
        authors: "McCormack & McFadden",
        journal: "Oxidative Medicine and Cellular Longevity",
        year: 2013,
        doi: "10.1155/2013/575482",
        finding:
          "Comprehensive review of pterostilbene's antioxidant, anti-inflammatory and anti-carcinogenic activity vs. resveratrol.",
      },
      {
        title:
          "Analysis of safety from a human clinical trial with pterostilbene",
        authors: "Riche, McEwen, Riche et al.",
        journal: "Journal of Toxicology",
        year: 2013,
        doi: "10.1155/2013/463595",
        finding:
          "Pterostilbene at 50–125mg/day for 6–8 weeks was safe in adults; reduced LDL and blood pressure in supplemental subgroups.",
      },
    ],
  },
  {
    id: "pqq",
    code: "03",
    name: "Pyrroloquinoline Quinone",
    short: "PQQ",
    dose: "20mg",
    role: "Mitochondrial optimization",
    thesis:
      "PQQ is a redox-active cofactor that triggers mitochondrial biogenesis through PGC-1α signaling — increasing the number and quality of cellular power units.",
    mechanism: [
      "Activates PGC-1α and CREB → mitochondrial biogenesis.",
      "Functions as a long-lived redox cofactor in cellular respiration.",
      "Reduces inflammatory markers (CRP, IL-6) in human supplementation trials.",
    ],
    studies: [
      {
        title:
          "Pyrroloquinoline quinone stimulates mitochondrial biogenesis through cAMP response element-binding protein phosphorylation and increased PGC-1α expression",
        authors: "Chowanadisai, Bauerly, Tchaparian et al.",
        journal: "Journal of Biological Chemistry",
        year: 2010,
        doi: "10.1074/jbc.M109.030130",
        finding:
          "PQQ induces mitochondrial biogenesis in cultured cells and in mice via CREB and PGC-1α activation.",
      },
      {
        title:
          "Dietary pyrroloquinoline quinone (PQQ) alters indicators of inflammation and mitochondrial-related metabolism in human subjects",
        authors: "Harris, Bashmakov, Bashmakova et al.",
        journal: "The Journal of Nutritional Biochemistry",
        year: 2013,
        doi: "10.1016/j.jnutbio.2013.07.008",
        finding:
          "20mg PQQ for 3 days reduced plasma CRP, IL-6 and altered urinary metabolites linked to mitochondrial function.",
      },
    ],
  },
  {
    id: "fisetin",
    code: "04",
    name: "Fisetin",
    short: "Fisetin",
    dose: "25mg",
    role: "Cellular maintenance",
    thesis:
      "A naturally occurring flavonol identified as the most potent senolytic in a screen of 10 flavonoids — supporting clearance of senescent cellular noise.",
    mechanism: [
      "Selectively induces apoptosis of senescent cells in vitro and in vivo.",
      "Modulates PI3K/AKT/mTOR and reduces SASP cytokine output.",
      "Improves tissue homeostasis markers in aged murine models.",
    ],
    studies: [
      {
        title:
          "Fisetin is a senotherapeutic that extends health and lifespan",
        authors: "Yousefzadeh, Zhu, McGowan et al.",
        journal: "EBioMedicine",
        year: 2018,
        doi: "10.1016/j.ebiom.2018.09.015",
        finding:
          "Fisetin reduced senescent cell burden in multiple tissues of aged mice and extended median + maximum lifespan.",
      },
      {
        title:
          "The flavonoid fisetin: from dietary intake to clinical relevance",
        authors: "Khan, Syed, Ahmad et al.",
        journal: "Antioxidants & Redox Signaling",
        year: 2013,
        doi: "10.1089/ars.2012.4901",
        finding:
          "Reviews fisetin's neuroprotective, anti-inflammatory and anti-cancer activity across preclinical models.",
      },
    ],
  },
  {
    id: "ergothioneine",
    code: "05",
    name: "Ergothioneine",
    short: "Ergothioneine",
    dose: "5mg",
    role: "Redox resilience",
    thesis:
      "A rare amino-thione actively transported into mitochondria-dense tissue via a dedicated transporter (OCTN1/SLC22A4) — implicated as a longevity vitamin.",
    mechanism: [
      "Accumulates in cells with the highest oxidative load: liver, kidney, eye, brain.",
      "Scavenges hydroxyl radicals, singlet oxygen and hypochlorite.",
      "Plasma ET levels decline with age and correlate with frailty markers.",
    ],
    studies: [
      {
        title:
          "Ergothioneine — a diet-derived antioxidant with therapeutic potential",
        authors: "Cheah & Halliwell",
        journal: "FEBS Letters",
        year: 2012,
        doi: "10.1016/j.febslet.2012.08.002",
        finding:
          "Comprehensive review of ergothioneine biochemistry, OCTN1-mediated transport and proposed role in redox defense.",
      },
      {
        title:
          "Is ergothioneine a 'longevity vitamin' limited in the American diet?",
        authors: "Beelman, Kalaras, Phillips et al.",
        journal: "Journal of Nutritional Science",
        year: 2020,
        doi: "10.1017/jns.2020.44",
        finding:
          "Argues ergothioneine intake correlates inversely with all-cause mortality and may be conditionally essential for healthy aging.",
      },
    ],
  },
];
